import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import SortTabs from "@/components/base/SortTabs";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import SoftwareCard from "@/components/base/SoftwareCard";
import Toast from "@/components/base/Toast";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  allCategories,
  filterSoftware,
  paginate,
  totalPages,
} from "@/utils/software";
import type { Software, SortKey } from "@/utils/software";

const PAGE_SIZE = 6;

export default function Softwares() {
  const [categoryId, setCategoryId] = useState("全部");
  const [sort, setSort] = useState<SortKey>("latest");
  const [layout, setLayout] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);
  const [toast, setToast] = useState<string | null>(null);

  useDocumentMeta({
    title: "全部黄金投资软件免费下载 - 黄金网站软件app免费",
    description:
      "浏览黄金网站软件app免费收录的全部黄金投资软件，支持按类型筛选、按发布时间与下载量排序，快速找到合适的黄金行情与理财工具。",
    keywords: "黄金投资软件大全,黄金软件免费下载,黄金行情软件,黄金理财工具",
  });

  const results = filterSoftware({
    categoryId: categoryId === "全部" ? undefined : categoryId,
    sort,
  });
  const pageCount = totalPages(results.length, PAGE_SIZE);
  const paged = paginate(results, page, PAGE_SIZE);

  const pickCategory = (value: string) => {
    setCategoryId(value);
    setPage(1);
  };
  const changeSort = (value: SortKey) => {
    setSort(value);
    setPage(1);
  };
  const resetFilters = () => {
    setCategoryId("全部");
    setSort("latest");
    setPage(1);
  };
  const handleDownload = (item: Software) => {
    setToast(`已打开「${item.name}」的示范下载链接（演示功能，无真实下载）`);
  };

  return (
    <SiteLayout>
      <div className="container-page py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "全部软件" }]} />

        <div className="mt-6 flex flex-col gap-2">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            全部黄金投资软件
          </h1>
          <p className="max-w-3xl text-sm leading-relaxed text-foreground-600">
            共收录 {results.length} 款黄金投资软件与 APP，覆盖趋势分析、价格监控、理财工具与行情资讯，全部免费下载，均为演示资源。
          </p>
        </div>

        <section className="card-surface mt-6 flex flex-col gap-5 p-5">
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">
              软件类型
            </span>
            <div className="flex flex-wrap gap-2">
              {["全部", ...allCategories.map((c) => c.name)].map((name) => {
                const cat = allCategories.find((c) => c.name === name);
                const value = cat ? cat.id : "全部";
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => pickCategory(value)}
                    className={`flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      categoryId === value
                        ? "border-primary-500 bg-primary-500 text-foreground-950"
                        : "border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-700"
                    }`}
                  >
                    {cat && <i className={cat.icon} />}
                    {name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-5">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-50 p-1">
                <span className="rounded-full bg-primary-500 px-3 py-1.5 text-sm font-medium text-foreground-950 whitespace-nowrap">
                  仅看免费
                </span>
                <span className="rounded-full px-3 py-1.5 text-sm font-medium text-foreground-600 whitespace-nowrap">
                  全部价格
                </span>
              </div>
              <span className="text-xs text-foreground-500">本站软件均为免费资源</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <SortTabs value={sort} onChange={changeSort} />
              <div className="inline-flex items-center gap-1 rounded-md border border-background-300 bg-background-50 p-1">
                <button
                  type="button"
                  onClick={() => setLayout("grid")}
                  aria-label="网格视图"
                  className={`flex h-8 w-8 items-center justify-center rounded transition-colors cursor-pointer ${
                    layout === "grid" ? "bg-secondary-900 text-background-50" : "text-foreground-500"
                  }`}
                >
                  <i className="ri-grid-fill" />
                </button>
                <button
                  type="button"
                  onClick={() => setLayout("list")}
                  aria-label="列表视图"
                  className={`flex h-8 w-8 items-center justify-center rounded transition-colors cursor-pointer ${
                    layout === "list" ? "bg-secondary-900 text-background-50" : "text-foreground-500"
                  }`}
                >
                  <i className="ri-list-check-2" />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-6 flex flex-col gap-4">
          {paged.length > 0 ? (
            <div
              className={
                layout === "grid"
                  ? "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3"
                  : "flex flex-col gap-4"
              }
              data-product-shop
            >
              {paged.map((item) => (
                <SoftwareCard
                  key={item.id}
                  item={item}
                  layout={layout}
                  onDownload={handleDownload}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              title="暂无相关软件"
              desc="当前筛选条件下没有匹配的软件，重置筛选后再试试吧。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          )}

          <Pagination page={page} total={pageCount} onChange={setPage} />
        </section>
      </div>
      <Toast message={toast} onClose={() => setToast(null)} />
    </SiteLayout>
  );
}